/**
 * Auto-generated React Query hooks from the OpenAPI v4 (student) spec
 * Do not edit this file manually - it will be overwritten
 *
 * Generated on: 2026-08-28T09:42:24.712Z
 */

import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { useApiDelete, useApiPatch, useApiPost, useApiPut, useApiQuery } from '../v4/hooks';
import type { paths } from './openapi-v4';
import type {
  DeleteResponse,
  GetResponse,
  PatchResponse,
  PostResponse,
  PutResponse,
} from './responses-v4';

/**
 * Claims a giveaway activation code using the claim_token from Zalo z_src (claim_tok). Requires student authentication (sess_tok auto-login or normal session). Generates the giveaway activation code and entitlement.
 * @tags student-giveaways
 * @path /v4/student/giveaways/claim
 * @method POST
 */
export function useStudentGiveawayClaim(
  options?: Omit<UseMutationOptions<PostResponse<'/v4/student/giveaways/claim'>, Error, any>, 'mutationFn'>,
) {
  return useApiPost('/v4/student/giveaways/claim', options);
}

/**
 * After you finish a test, what you should do next. Pass the check point you just finished and the answer stays inside that test level and that type of test: `next` is the check point right after it by position (null when you are at the end of the level), and `suggestions` are the other check points in the level, the ones you have not attempted listed first. NO LOGIN needed — the result screen links here — but log in and the suggestions are ordered by what YOU have already done, and premium check points you cannot use are left out. A single object, not a page. **Lỗi có thể trả về.** Client rẽ nhánh theo `code` và chỉ theo `code` — `detail` là tiếng Anh dành cho developer và câu chữ có thể thay đổi bất cứ lúc nào. `message` là câu tiếng Việt đã viết sẵn cho người dùng, dùng được ngay khi client chưa có bản dịch riêng cho `code` đó. | HTTP | `code` | Khi nào xảy ra | | --- | --- | --- | | 401 | `UNAUTHORIZED` | Bạn cần đăng nhập lại. | | 403 | `FORBIDDEN` | Bạn không có quyền thực hiện việc này. | | 404 | `JOURNEY_CHECK_POINT_NOT_FOUND` | Không tìm thấy bài kiểm tra. | | 422 | `VALIDATION_FAILED` | Dữ liệu gửi lên không hợp lệ. | | 500 | `INTERNAL_ERROR` | Có lỗi xảy ra, vui lòng thử lại sau. |
 * @tags student-journey
 * @path /v4/student/journey/next-check-point
 * @method GET
 */
export function useStudentGetNextCheckPoint(
  params: {
    query?: paths['/v4/student/journey/next-check-point']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/journey/next-check-point'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/journey/next-check-point', params, options);
}

/**
 * The gifts one student has won with wisdom stones, newest first. NO LOGIN: this is the only student endpoint that needs no session, so it can be embedded in a share page or shown beside a student who is not the one holding the phone. OWNED means APPROVED — a virtual gift from the moment it was bought, a physical one once a teacher handed it over. Orders still waiting for a teacher, refused, or cancelled are not here. ACROSS EVERY SCHOOL the student has attended, unlike /wisdom-stones/store/redemptions, which answers out of the wallet at the school of their most recently joined class. A student who has left every class still owns their awards, and this list still shows them. NOTHING IDENTIFYING COMES BACK — no name, no avatar, no school, no class, no balance and no price — so a client that wants to title the page has to know the student by other means. An id that belongs to nobody, and a student with no awards yet, both answer 200 with an empty list and total_count 0; there is no 404 that would tell the two apart.
 * @tags student-wisdom-stones-public
 * @path /v4/student/public/wisdom-stones/awards/{learner_profile_id}
 * @method GET
 */
export function useStudentListPublicAwards(
  params: {
    path: paths['/v4/student/public/wisdom-stones/awards/{learner_profile_id}']['get']['parameters']['path'];
    query?: paths['/v4/student/public/wisdom-stones/awards/{learner_profile_id}']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/public/wisdom-stones/awards/{learner_profile_id}'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/public/wisdom-stones/awards/{learner_profile_id}', params, options);
}

/**
 * Sends the in-app notification that a recap is ready, to EVERY student at once — the same one the schedule sends at 02:00 on the first of each month. Requires the ADMIN staff role; every other role, staff or not, gets 403. Name the month that ENDED: month 1–11 sends that month's recap, month 12 sends the recap of that whole year. It writes one global notification and nothing else; it does not compute the recap numbers, which are already there. Sending the same period twice is safe and writes nothing the second time — the response says which happened. This is an in-app notification, not a push.
 * @tags student-recaps
 * @path /v4/student/recaps/announcements
 * @method POST
 */
export function useStudentAnnounceRecap(
  options?: Omit<UseMutationOptions<PostResponse<'/v4/student/recaps/announcements'>, Error, any>, 'mutationFn'>,
) {
  return useApiPost('/v4/student/recaps/announcements', options);
}

/**
 * What you did in one calendar month and where it put you among every learner on the platform — the numbers the end-of-month recap draws. A single object, not a page. Did you sit no test that month and data is null; the screen shows zeros. These numbers are refreshed periodically rather than live, so the month currently running reports what it held at the last refresh. Ask for a month that has ended.
 * @tags student-scores
 * @path /v4/student/scores/monthly
 * @method GET
 */
export function useStudentGetMonthlyScore(
  params: {
    query?: paths['/v4/student/scores/monthly']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/scores/monthly'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/scores/monthly', params, options);
}

/**
 * What you did across one year and where it put you among every learner — the numbers the year-end recap draws, including the percentile band it shows as 'top N%'. A single object, not a page. Not a longer version of the monthly recap: a year counts your BEST attempt at each check point rather than every attempt, ranks by total_grade rather than by score, and runs on Vietnam local time rather than UTC. Did you sit no test that year and data is null. A year is computed once, after it ends, so ask for a year that has finished.
 * @tags student-scores
 * @path /v4/student/scores/yearly
 * @method GET
 */
export function useStudentGetYearlyScore(
  params: {
    query?: paths['/v4/student/scores/yearly']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/scores/yearly'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/scores/yearly', params, options);
}

/**
 * The server's current time, as one instant in UTC, in the server's local zone, and as Unix seconds. For clients that must not trust the device clock.
 * @tags student-time
 * @path /v4/student/time
 * @method GET
 */
export function useStudentGetServerTime(options?: Omit<UseQueryOptions<GetResponse<'/v4/student/time'>>, 'queryKey' | 'queryFn'>) {
  return useApiQuery('/v4/student/time', undefined, options);
}

/**
 * How many stones you hold, at the ONE school you are at — a single object, not a page, with your own profile on it so the screen can draw your name and face beside the number. The school is the one your most recently joined class belongs to. Are you in no class at any school and data is null: the stones stay recorded and come back as soon as a teacher puts you in a class there again. Balances are never summed across schools: a student in two schools has two independent wallets and no teacher can see or spend the other's. A school you are at but have never been given stones at reports 0, so a new student sees their school rather than an empty screen. The history endpoint resolves the same school the same way, so it needs nothing from this response.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/balances
 * @method GET
 */
export function useStudentGetWisdomStoneBalance(options?: Omit<UseQueryOptions<GetResponse<'/v4/student/wisdom-stones/balances'>>, 'queryKey' | 'queryFn'>) {
  return useApiQuery('/v4/student/wisdom-stones/balances', undefined, options);
}

/**
 * Every movement of YOUR balance at one school, newest first — stones given to you individually, stones from a grant to your group, and stones taken back. Each row carries the teacher who did it and you, both with the name and avatar to render, plus the group when it was a group grant. The school is the one your most recently joined class belongs to — the SAME school the balances endpoint reports, so the two always agree and there is nothing to pass in. balance_after therefore still reads as a running total: the list is one school's wallet. Are you in no class at any school and the page is empty. Add ?kind=GRANTED to see only what you gained, and created_from / created_to to see one day, one week or one term. Both ends of that window are inclusive, and either can be left out.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/histories
 * @method GET
 */
export function useStudentListWisdomStoneHistory(
  params?: {
    query?: paths['/v4/student/wisdom-stones/histories']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/wisdom-stones/histories'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/wisdom-stones/histories', params, options);
}

/**
 * Every gift you can spend stones on, with your own wallet beside it. FLYER gifts come first — the ones every student sees — then your centre's and your class's. Each card already says whether you own it, whether it is sold out, whether you can afford it and how many more stones you would need, so nothing has to be worked out client-side. One flat page rather than two nested groups, so total_count is a real count: group by `scope` to draw the two sections, or ask for one with ?scope=FLYER. The school is the one your most recently joined class belongs to — the SAME school the balances and history endpoints use. Are you in no class at any school and the page is empty with a null balance: without a school there is no wallet, so there would be nothing you could buy.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/store/items
 * @method GET
 */
export function useStudentListStoreItems(
  params?: {
    query?: paths['/v4/student/wisdom-stones/store/items']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/wisdom-stones/store/items'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/wisdom-stones/store/items', params, options);
}

/**
 * Your own orders at one school, newest first — what you spent stones on, and where each order stands. A PENDING order is the clock icon: the stones are already gone and a teacher has not handed the gift over yet, and you can still call it off. A REJECTED order names the reason and its stones were returned. Every gift detail is what it looked like when you bought it, so a gift the centre has since renamed or deleted still renders — item_id is null once it is gone, and price is what you actually paid. THE AWARDS YOU OWN ARE ?status=APPROVED. That is the whole list of gifts that are yours — a virtual one from the moment you bought it, a physical one once a teacher handed it over — and there is no separate endpoint for it, because it is this list with one value chosen. ?status=PENDING is the mirror of that: bought, paid for, still waiting. The school is resolved as everywhere else here; are you in no class and the page is empty.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/store/redemptions
 * @method GET
 */
export function useStudentListStoreRedemptions(
  params?: {
    query?: paths['/v4/student/wisdom-stones/store/redemptions']['get']['parameters']['query'];
  },
  options?: Omit<UseQueryOptions<GetResponse<'/v4/student/wisdom-stones/store/redemptions'>>, 'queryKey' | 'queryFn'>,
) {
  return useApiQuery('/v4/student/wisdom-stones/store/redemptions', params, options);
}

/**
 * Spend stones on one gift. A VIRTUAL gift is yours immediately — the order comes back APPROVED and is final, so there is nothing to cancel. A PHYSICAL gift comes back PENDING and waits for a teacher, and you can call it off until they act. The stones leave your wallet NOW in both cases, and the unit is held for you, which is why a pending order still counts against stock. You send only item_id: the wallet, the class recorded on the order and the price charged are all resolved from your session, and the price is the one shown when the unit was held. 409 if the gift is sold out, has been hidden or deleted, or you already hold as many as the gift allows. 422 if you do not have enough stones, or you are in no class and so have no wallet. 404 if the gift is not one your school or class offers.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/store/redemptions
 * @method POST
 */
export function useStudentCreateStoreRedemption(
  options?: Omit<UseMutationOptions<PostResponse<'/v4/student/wisdom-stones/store/redemptions'>, Error, any>, 'mutationFn'>,
) {
  return useApiPost('/v4/student/wisdom-stones/store/redemptions', options);
}

/**
 * Cancel an order that is still waiting for a teacher. The stones come back — exactly what you paid, not what the gift costs today — and the unit goes back on the shelf. Only a PENDING order can be cancelled, which means only a physical gift: a virtual gift is settled the moment you buy it and is final. 409 if the order is not yours, is not waiting any more, or you have already cancelled it — so a double tap is safe and cannot refund twice.
 * @tags student-wisdom-stones
 * @path /v4/student/wisdom-stones/store/redemptions/{redemption_id}/cancel
 * @method POST
 */
export function useStudentCancelStoreRedemption(
  options?: Omit<UseMutationOptions<PostResponse<'/v4/student/wisdom-stones/store/redemptions/{redemption_id}/cancel'>, Error, any>, 'mutationFn'>,
) {
  return useApiPost('/v4/student/wisdom-stones/store/redemptions/{redemption_id}/cancel', options);
}
